import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Input } from "../../../components/ui/input/SimpleInput";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";

import {
  CHARGE_MODE_LABELS,
  ChargeFields,
  DISTRIBUTION_RIGHTS_LABELS,
  PublicChargeRegistry,
  RegistryIdentifiers,
  SECURITY_ROLE_LABELS,
  SecurityChargeMode,
  SecurityDistributionRights,
  SecurityRole,
} from "../../../api/charges";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import HorizontalRadioButtonsInput, { RadioOption } from "../../../components/ui/button/v2/HorizontalRadioButtonsInput";

interface ChargeInformationStepProps {
  chargeInformation: ChargeFields;
  onChange: (updates: Partial<ChargeFields>) => void;
}

const SECTION_TITLE_CLASS ="text-[14px] font-medium leading-5 text-[#0A0A0A]";

const FIELD_LABEL_CLASS ="block text-[14px] font-medium text-[#0A0A0A]";

const INLINE_FIELD_LABEL_CLASS ="text-[14px] font-medium leading-5 text-[#0A0A0A]";

const INPUT_CLASS ="h-9 rounded-[8px] border border-[#E5E5E5] bg-white px-3 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.05)]";

const SELECT_CONTAINER_CLASS = "w-full";

const SELECT_LABEL_CLASS ="h-9 w-full rounded-[8px] border border-[#E5E5E5] bg-white px-3 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.05)]";

const securityRoleOptions: OptionNameValuePair<SecurityRole>[] =
  Object.values(SecurityRole).map(value => ({
    name: SECURITY_ROLE_LABELS[value],
    value,
  }));

const chargeModeOptions: OptionNameValuePair<SecurityChargeMode>[] =
  Object.values(SecurityChargeMode).map(value => ({
    name: CHARGE_MODE_LABELS[value],
    value,
  }));

const distributionRightsOptions: OptionNameValuePair<SecurityDistributionRights>[] =
  Object.values(SecurityDistributionRights).map(value => ({
    name: DISTRIBUTION_RIGHTS_LABELS[value],
    value,
  }));  

const publicRegistryOptions: RadioOption<PublicChargeRegistry>[] = [
  {
    name: "CERSAI",
    value: PublicChargeRegistry.CERSAI,
  },
  {
    name: "ROC",
    value: PublicChargeRegistry.ROC,
    disabled: true,
  },
];

const ChargeInformationStep: React.FC<
  ChargeInformationStepProps
> = ({
  chargeInformation,
  onChange,
}) => {
  const [isExpanded, setIsExpanded] =useState(true);

  const updateRegistryIdentifiers = (
    updates: RegistryIdentifiers,
  ) => {
    onChange({
      registry_identifiers: {
        ...chargeInformation.registry_identifiers,
        ...updates,
      },
    });
  };

  return (
    <div className="rounded-[8px] bg-[#F3F4F6] p-3">
      <div className="flex items-center justify-between">
        <span className={SECTION_TITLE_CLASS}>
          Attributes
        </span>

        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          className="flex h-4 w-4 items-center justify-center"
        >
          {isExpanded ? (
            <ChevronUp
              size={16}
              className="text-[#737373]"
            />
          ) : (
            <ChevronDown
              size={16}
              className="text-[#737373]"
            />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="pt-5">
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <label className={INLINE_FIELD_LABEL_CLASS}>
                Choose Public Registry
                <span className="text-[#DC2626]">*</span>
              </label>

              <HorizontalRadioButtonsInput
                options={publicRegistryOptions}
                selectedValue={chargeInformation.public_registry}
                onSelectionChange={(value) =>
                  onChange({
                    public_registry: value,
                  })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={FIELD_LABEL_CLASS}>
                  Security Role
                  <span className="text-[#DC2626]">*</span>
                </label>

                <SelectDropdownV2
                  name="Security Role"
                  enableSearch={false}
                  options={securityRoleOptions}
                  selectedOption={securityRoleOptions.find(
                    option =>
                      option.value ===
                      chargeInformation.security_role,
                  )}
                  onSelectionChange={option =>
                    onChange({
                      security_role: option.value,
                    })
                  }
                  containerClassName={SELECT_CONTAINER_CLASS}
                  labelClassName={SELECT_LABEL_CLASS}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className={FIELD_LABEL_CLASS}>
                  Charge Mode
                  <span className="text-[#DC2626]">*</span>
                </label>

                <SelectDropdownV2
                  name="Charge Mode"
                  enableSearch={false}
                  options={chargeModeOptions}
                  selectedOption={chargeModeOptions.find(
                    option =>
                      option.value ===
                      chargeInformation.charge_mode,
                  )}
                  onSelectionChange={option =>
                    onChange({
                      charge_mode: option.value,
                    })
                  }
                  containerClassName={SELECT_CONTAINER_CLASS}
                  labelClassName={SELECT_LABEL_CLASS}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className={FIELD_LABEL_CLASS}>
                  Distribution Rights
                  <span className="text-[#DC2626]">*</span>
                </label>

                <SelectDropdownV2
                  name="Distribution Rights"
                  enableSearch={false}
                  options={distributionRightsOptions}
                  selectedOption={distributionRightsOptions.find(
                    option =>
                      option.value ===
                      chargeInformation.distribution_rights,
                  )}
                  onSelectionChange={option =>
                    onChange({
                      distribution_rights:
                        option.value,
                    })
                  }
                  containerClassName={SELECT_CONTAINER_CLASS}
                  labelClassName={SELECT_LABEL_CLASS}
                />
              </div>

              {chargeInformation.public_registry ===
                PublicChargeRegistry.CERSAI && (
                <div className="flex flex-col gap-2 ">
                  <label className="flex items-start text-[14px] font-medium leading-5 text-[#0A0A0A]">
                    Security Interest ID
                    <span className="text-[#DC2626]">*</span>
                  </label>

                  <Input
                    type="text"
                    placeholder="Enter security interest ID"
                    value={
                      chargeInformation
                        .registry_identifiers
                        ?.security_interest_id ?? ""
                    }
                    onChange={e =>
                      updateRegistryIdentifiers({
                        security_interest_id:
                          e.target.value,
                      })
                    }
                    className={INPUT_CLASS}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargeInformationStep;